'use client'

import { type FC, useEffect, useRef } from 'react'
import { FormattedMessage } from 'react-intl'

import { IconPlus } from '@redteclab/icons'

import { Button, Divider } from 'base-ui'

import { adServerSlotPushSetupPreloadedSlotsCommand } from '../../../ad-server'
import { useGlobalConfigContext } from '../../../global-config'
import { productSearchAlgoliaAccessorIsRxCh } from '../../model/productSearchAlgoliaAccessor/productSearchAlgoliaAccessorIsRxCh'

import { useProductSearchResultItemContext } from './ProductSearchResultItemContext/productSearchResultItemContext'
import { ProductSearchResultItemFacts } from './ProductSearchResultItemFacts/ProductSearchResultItemFacts'
import { ProductSearchResultItemImage } from './ProductSearchResultItemImage'
import { ProductSearchResultItemLabelList } from './ProductSearchResultItemLabelList'
import { ProductSearchResultItemMarketplaceSeller } from './ProductSearchResultItemMarketplaceSeller'
import { ProductSearchResultItemPrescriptionProductLinkWithIcon } from './ProductSearchResultItemPrescriptionProductLinkWithIcon'
import { ProductSearchResultItemPriceContainer } from './ProductSearchResultItemPriceContainer/ProductSearchResultItemPriceContainer'
import { ProductSearchResultItemReview } from './ProductSearchResultItemReview'
import { ProductSearchResultItemStockStatus } from './ProductSearchResultItemStockStatus'
import { ProductSearchResultItemTitle } from './ProductSearchResultItemTitle'

export const ProductSearchResultItem: FC = () => {
  const itemRef = useRef<HTMLDivElement | null>(null)
  const { tenant } = useGlobalConfigContext()

  const { algoliaRecord } = useProductSearchResultItemContext()

  // For Rx products on CH we don't show price only a 'special' link to PDP
  const isRxChProduct = productSearchAlgoliaAccessorIsRxCh({
    algoliaRecord,
    tenant,
  })

  const { adServerMetadata, descriptionShort, variants } = algoliaRecord

  useEffect(() => {
    if (adServerMetadata && itemRef.current) {
      adServerSlotPushSetupPreloadedSlotsCommand({
        metadataContent: adServerMetadata,
        slot: itemRef.current,
      })
    }
  }, [adServerMetadata, itemRef])

  return (
    <div
      className="relative z-0 flex cursor-pointer items-start gap-4 rounded-2xl border border-dark-primary-min p-4 pt-6"
      ref={itemRef}
    >
      <ProductSearchResultItemLabelList />

      <ProductSearchResultItemImage />

      <div className="flex flex-1 flex-col gap-3 desktop:flex-row">
        <div className="flex flex-1 flex-col items-start gap-4">
          <div className="flex flex-col gap-1">
            <ProductSearchResultItemTitle />
            <ProductSearchResultItemReview />
          </div>

          <ProductSearchResultItemFacts />
          <ProductSearchResultItemStockStatus />
        </div>
        <Divider className="hidden desktop:block desktop:h-auto desktop:flex-col" />
        <div className="flex flex-1 flex-col items-start gap-4">
          {descriptionShort ? (
            <p className="hidden self-stretch text-xs desktop:block">
              {descriptionShort}
            </p>
          ) : null}

          {isRxChProduct ? (
            <div className="flex flex-1 flex-col gap-3 self-end">
              <ProductSearchResultItemPrescriptionProductLinkWithIcon />
            </div>
          ) : (
            <div className="flex flex-1 flex-col items-start gap-3">
              <ProductSearchResultItemPriceContainer />
              <ProductSearchResultItemMarketplaceSeller />
            </div>
          )}

          {variants && variants.length > 1 ? (
            <div className="flex flex-1 flex-col items-start gap-3">
              <Button
                className="font-normal"
                size="small"
                slots={{
                  root: 'span',
                }}
                startIcon={<IconPlus />}
                variant="text"
              >
                <FormattedMessage id="product.otherPacksizes" />
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
